import React, { useState, useEffect } from 'react';
import './App.css';

// Layout Components
import { Navigation } from './components/Layout/Navigation';
import { Footer } from './components/Layout/Footer';

// Section Components  
import { HeroSection } from './components/Sections/HeroSection';
import { PortfolioSection } from './components/Sections/PortfolioSection';
import { ServicesSection } from './components/Sections/ServicesSection';
import { TestimonialsSection } from './components/Sections/TestimonialsSection';
import { AboutSection } from './components/Sections/AboutSection';
import { ContactSection } from './components/Sections/ContactSection';
import { FaqSection } from './components/Sections/FaqSection';

// UI Components
import { ImageModal } from './components/UI/ImageModal';

// Hooks
import { useScrollY } from './hooks/useScrollY';

// Data
import { portfolioImages } from './data/portfolioImages';
import { categories } from './data/categories';
import { setSessionStorageItem } from './utils/browserStorage';
import { getBookingWindowLabel } from './utils/bookingWindow';
import { testimonials } from './data/testimonials';

function App() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactFormInView, setIsContactFormInView] = useState(false);
  const [isHeroInView, setIsHeroInView] = useState(true);
  const scrollY = useScrollY();

  const [bookingWindowLabel, setBookingWindowLabel] = useState(() => getBookingWindowLabel(new Date()));

  const handleAvailabilityClick = () => {
    if (typeof window !== 'undefined') {
      setSessionStorageItem('hero_focus_contact_form', '1');
      window.dispatchEvent(new CustomEvent('hero:availability-click'));
    }
  };

  const localBusinessStructuredData = React.useMemo(() => {
    const reviewCount = testimonials.length;
    const averageRating =
      reviewCount > 0
        ? (testimonials.reduce((sum, testimonial) => sum + testimonial.rating, 0) / reviewCount).toFixed(1)
        : null;
    const reviews = testimonials.slice(0, 3).map((testimonial) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: testimonial.name
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(testimonial.rating),
        bestRating: '5',
        worstRating: '1'
      },
      reviewBody: testimonial.text
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Cassidy Sullivan Photography',
      '@id': 'https://www.cassidysullivanphotography.com/#localbusiness',
      url: 'https://www.cassidysullivanphotography.com/',
      telephone: '+1-606-922-6307',
      email: 'cassidy@cassidysullivanphotography.com',
      image: 'https://www.cassidysullivanphotography.com/images/hero-dauntless.jpg',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          telephone: '+1-606-922-6307',
          email: 'cassidy@cassidysullivanphotography.com',
          areaServed: 'US-KY',
          availableLanguage: 'en'
        }
      ],
      description:
        'Equine-first photography studio serving Lexington, Kentucky with equine, portrait, family, and wedding photography.',
      priceRange: '$$-$$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '09:00',
          closes: '18:00'
        }
      ],
      areaServed: [
        {
          '@type': 'City',
          name: 'Lexington'
        },
        {
          '@type': 'AdministrativeArea',
          name: 'Kentucky'
        }
      ],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Lexington',
        addressRegion: 'KY',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 38.0406,
        longitude: -84.5037
      },
      hasMap: 'https://maps.google.com/?q=Lexington,+KY',
      aggregateRating:
        averageRating !== null
          ? {
              '@type': 'AggregateRating',
              ratingValue: averageRating,
              reviewCount: String(reviewCount),
              bestRating: '5',
              worstRating: '1'
            }
          : undefined,
      review: reviews.length > 0 ? reviews : undefined,
      sameAs: [
        'https://www.instagram.com/cassidysullivanphotography/',
        'https://www.facebook.com/CSullivanPortraits/'
      ],
      makesOffer: [
        'Equine Photography',
        'Black Background Equine',
        'Wedding Photography',
        'Family Photography',
        'Portrait Sessions'
      ].map((serviceName) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: serviceName
        }
      }))
    };
  }, []);

  useEffect(() => {
    const refreshBookingWindow = () => {
      setBookingWindowLabel(getBookingWindowLabel(new Date()));
    };

    refreshBookingWindow();
    const intervalId = window.setInterval(refreshBookingWindow, 60 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const contactForm = document.getElementById('contact-form');

    if (!contactForm) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsContactFormInView(entry.isIntersecting);
      },
      {
        threshold: 0.2
      }
    );

    observer.observe(contactForm);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const heroSection = document.getElementById('home');

    if (!heroSection) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroInView(entry.isIntersecting);
      },
      {
        threshold: 0.25
      }
    );

    observer.observe(heroSection);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Filter images based on current category
  const filteredImages = currentCategory === 'all' 
    ? portfolioImages 
    : portfolioImages.filter(img => img.category === currentCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 text-gray-900 pb-24 md:pb-0">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-slate-900 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      <Navigation 
        scrollY={scrollY}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessStructuredData) }} />

      <main id="main-content" tabIndex={-1}>
        <div className="pt-16">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-3 font-semibold text-white shadow-sm">
            <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 text-center sm:flex-row sm:gap-4">
              <p>
                <span className="font-extrabold">Now Booking:</span> {bookingWindowLabel} equine, portrait, and family sessions across Kentucky.
              </p>
              <a
                href="#contact-form"
                onClick={handleAvailabilityClick}
                className="inline-flex items-center rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-cyan-700"
              >
                Check Availability
              </a>
            </div>
          </div>
        </div>
        
        <HeroSection scrollY={scrollY} />
        
        <PortfolioSection 
          currentCategory={currentCategory}
          setCurrentCategory={setCurrentCategory}
          filteredImages={filteredImages}
          setSelectedImage={setSelectedImage}
          categories={categories}
        />
        
        <ServicesSection />
        
        <TestimonialsSection />

        <FaqSection />
        
        <AboutSection />
        
        <ContactSection />
        
        <Footer />
      </main>

      {!isContactFormInView && !isHeroInView && !isMenuOpen && !selectedImage && (
        <a
          href="#contact-form"
          onClick={handleAvailabilityClick}
          aria-label="Book a photography session"
          className="fixed left-4 right-4 z-40 text-center rounded-full border border-white/30 bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-3 font-semibold text-white shadow-2xl transition-all duration-300 md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-cyan-700"
          style={{
            bottom: 'max(1rem, env(safe-area-inset-bottom))'
          }}
        >
          Book a Session
        </a>
      )}

      <ImageModal 
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        filteredImages={filteredImages}
      />
    </div>
  );
}

export default App;